import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { SERVICES } from "../data/services";
import { waLink } from "../config/site";

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-spark focus:ring-2 focus:ring-spark/20";

const labelCls = "mb-1.5 block font-mono text-xs uppercase tracking-[0.2em] text-slate-500";

export const InquiryForm = ({ initialService }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: initialService || SERVICES[0].slug,
    budget: "",
    message: "",
  });
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please add your name and project details.");
      return;
    }
    const svc = SERVICES.find((s) => s.slug === form.service)?.name || form.service;
    const msg = [
      `Hello SparkGrid Solutions! I would like to inquire about *${svc}*.`,
      "",
      `Name: ${form.name}`,
      form.email && `Email: ${form.email}`,
      form.phone && `Phone: ${form.phone}`,
      form.budget && `Budget: ${form.budget}`,
      "",
      "Project Details:",
      form.message,
    ]
      .filter(Boolean)
      .join("\n");
    window.open(waLink(msg), "_blank");
    toast.success("Opening WhatsApp with your inquiry…");
  };

  return (
    <form onSubmit={submit} data-testid="inquiry-form" className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="inq-name" className={labelCls}>Your Name *</label>
          <input
            id="inq-name"
            data-testid="contact-input-name"
            className={inputCls}
            placeholder="Rahul Sharma"
            value={form.name}
            onChange={set("name")}
          />
        </div>
        <div>
          <label htmlFor="inq-email" className={labelCls}>Email</label>
          <input
            id="inq-email"
            type="email"
            data-testid="contact-input-email"
            className={inputCls}
            placeholder="you@company.com"
            value={form.email}
            onChange={set("email")}
          />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="inq-phone" className={labelCls}>Phone</label>
          <input
            id="inq-phone"
            data-testid="contact-input-phone"
            className={inputCls}
            placeholder="+91 98XXX XXXXX"
            value={form.phone}
            onChange={set("phone")}
          />
        </div>
        <div>
          <label htmlFor="inq-service" className={labelCls}>Service Needed</label>
          <select
            id="inq-service"
            data-testid="contact-select-service"
            className={inputCls}
            value={form.service}
            onChange={set("service")}
          >
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.slug}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="inq-budget" className={labelCls}>Estimated Budget</label>
        <select
          id="inq-budget"
          data-testid="contact-select-budget"
          className={inputCls}
          value={form.budget}
          onChange={set("budget")}
        >
          <option value="">Select a range</option>
          <option>Under ₹50,000</option>
          <option>₹50,000 – ₹1,50,000</option>
          <option>₹1,50,000 – ₹5,00,000</option>
          <option>₹5,00,000+</option>
        </select>
      </div>
      <div>
        <label htmlFor="inq-message" className={labelCls}>Project Details *</label>
        <textarea
          id="inq-message"
          rows={4}
          data-testid="contact-input-message"
          className={`${inputCls} resize-none`}
          placeholder="Tell us about your idea, timeline and goals…"
          value={form.message}
          onChange={set("message")}
        />
      </div>
      <button
        type="submit"
        data-testid="inquiry-form-submit-btn"
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-spark to-ember px-6 py-4 text-sm font-bold text-night transition-transform duration-300 hover:scale-[1.02]"
      >
        Send Inquiry via WhatsApp
        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      <p className="text-center text-xs text-slate-400">
        Submitting opens WhatsApp with your details pre-filled — no sign-up needed.
      </p>
    </form>
  );
};
